/**
 * TASK-192: KYC Field Mapping Configuration
 *
 * Single source of truth for mapping AI extraction data to KYC database schema.
 * This configuration enables:
 * 1. Dynamic review UI (show only extracted sections/fields)
 * 2. Data validation against expected structure
 * 3. Type-safe field access
 * 4. Consistent field rendering across components
 */

// Icon names are stored as strings and resolved at render time
// Available icons: User, Briefcase, FileText, DollarSign, TrendingUp, Building, Flag, GraduationCap

/**
 * Field types for rendering in UI
 */
export type FieldType =
  | 'text'
  | 'email'
  | 'phone'
  | 'date'
  | 'number'
  | 'currency'
  | 'percentage'
  | 'address'
  | 'boolean'
  | 'select'
  | 'array'
  | 'textarea';

/**
 * Field definition for a single KYC field
 */
export interface FieldDefinition {
  /** Display label for the field */
  label: string;

  /** Path in extracted_data JSON (e.g., "personalInfo.firstName") */
  extractionPath: string;

  /** Database column name (snake_case, e.g., "first_name") */
  dbColumn: string;

  /** Field type for UI rendering */
  fieldType: FieldType;

  /** Is this field required? */
  required: boolean;

  /** Is this field encrypted in database? */
  encrypted: boolean;

  /** Help text to show in UI */
  helpText?: string;

  /** Options for select fields */
  options?: Array<{ value: string; label: string }>;

  /** Validation regex pattern */
  pattern?: string;

  /** Min/max for number fields */
  min?: number;
  max?: number;
}

/**
 * Section definition containing multiple fields
 */
export interface SectionDefinition {
  /** Unique section ID */
  id: string;

  /** Display title for section */
  title: string;

  /** Icon component name (lucide-react) */
  icon: string;

  /** Target database table */
  dbTable: string;

  /** Is this a one-to-many relationship? */
  isArray: boolean;

  /** Fields in this section */
  fields: FieldDefinition[];

  /** Display order */
  order: number;
}

/**
 * Complete KYC field mapping configuration
 */
export const KYC_FIELD_MAPPING: SectionDefinition[] = [
  // ========================================
  // SECTION 1: Personal Information
  // ========================================
  {
    id: 'personalInfo',
    title: 'Personal Information',
    icon: 'User',
    dbTable: 'kyc_profiles',
    isArray: false,
    order: 1,
    fields: [
      {
        label: 'First Name',
        extractionPath: 'personalInfo.firstName',
        dbColumn: 'first_name',
        fieldType: 'text',
        required: true,
        encrypted: true,
      },
      {
        label: 'Middle Name',
        extractionPath: 'personalInfo.middleName',
        dbColumn: 'middle_name',
        fieldType: 'text',
        required: false,
        encrypted: true,
      },
      {
        label: 'Last Name',
        extractionPath: 'personalInfo.lastName',
        dbColumn: 'last_name',
        fieldType: 'text',
        required: true,
        encrypted: true,
      },
      {
        label: 'Date of Birth',
        extractionPath: 'personalInfo.dateOfBirth',
        dbColumn: 'date_of_birth',
        fieldType: 'date',
        required: true,
        encrypted: false,
      },
      {
        label: 'Place of Birth',
        extractionPath: 'personalInfo.placeOfBirth',
        dbColumn: 'place_of_birth',
        fieldType: 'text',
        required: false,
        encrypted: false,
      },
      {
        label: 'Nationalities',
        extractionPath: 'personalInfo.nationalities',
        dbColumn: 'nationalities',
        fieldType: 'array',
        required: false,
        encrypted: false,
        helpText: 'ISO 2-letter country codes (e.g., CH, US)',
      },
      {
        label: 'Gender',
        extractionPath: 'personalInfo.gender',
        dbColumn: 'gender',
        fieldType: 'select',
        required: false,
        encrypted: false,
        options: [
          { value: 'male', label: 'Male' },
          { value: 'female', label: 'Female' },
          { value: 'other', label: 'Other' },
        ],
      },
      {
        label: 'Marital Status',
        extractionPath: 'personalInfo.maritalStatus',
        dbColumn: 'marital_status',
        fieldType: 'select',
        required: false,
        encrypted: false,
        options: [
          { value: 'single', label: 'Single' },
          { value: 'married', label: 'Married' },
          { value: 'divorced', label: 'Divorced' },
          { value: 'widowed', label: 'Widowed' },
        ],
      },
      {
        label: 'Passport Number',
        extractionPath: 'personalInfo.passportNumber',
        dbColumn: 'passport_number',
        fieldType: 'text',
        required: false,
        encrypted: true,
      },
      {
        label: 'National ID',
        extractionPath: 'personalInfo.nationalId',
        dbColumn: 'national_id',
        fieldType: 'text',
        required: false,
        encrypted: true,
      },
      {
        label: 'SSN',
        extractionPath: 'personalInfo.ssn',
        dbColumn: 'ssn',
        fieldType: 'text',
        required: false,
        encrypted: true,
        helpText: 'Social Security Number or equivalent',
      },
      {
        label: 'Former Names',
        extractionPath: 'personalInfo.formerNames',
        dbColumn: 'former_names',
        fieldType: 'array',
        required: false,
        encrypted: true,
        helpText: 'Previous legal names',
      },
      {
        label: 'Languages',
        extractionPath: 'personalInfo.languages',
        dbColumn: 'languages',
        fieldType: 'array',
        required: false,
        encrypted: false,
        helpText: 'Spoken languages (e.g., en, de, fr)',
      },
    ],
  },

  // ========================================
  // SECTION 2: Contact Information
  // ========================================
  {
    id: 'contactInfo',
    title: 'Contact Information',
    icon: 'FileText',
    dbTable: 'kyc_profiles',
    isArray: false,
    order: 2,
    fields: [
      {
        label: 'Primary Phone',
        extractionPath: 'contactInfo.phonePrimary',
        dbColumn: 'phone_primary',
        fieldType: 'phone',
        required: false,
        encrypted: true,
      },
      {
        label: 'Secondary Phone',
        extractionPath: 'contactInfo.phoneSecondary',
        dbColumn: 'phone_secondary',
        fieldType: 'phone',
        required: false,
        encrypted: true,
      },
      {
        label: 'Primary Email',
        extractionPath: 'contactInfo.emailPrimary',
        dbColumn: 'email_primary',
        fieldType: 'email',
        required: false,
        encrypted: false,
      },
      {
        label: 'Secondary Email',
        extractionPath: 'contactInfo.emailSecondary',
        dbColumn: 'email_secondary',
        fieldType: 'email',
        required: false,
        encrypted: false,
      },
      {
        label: 'Residential Address',
        extractionPath: 'contactInfo.residentialAddress',
        dbColumn: 'residential_address',
        fieldType: 'address',
        required: false,
        encrypted: false,
      },
      {
        label: 'Mailing Address',
        extractionPath: 'contactInfo.mailingAddress',
        dbColumn: 'mailing_address',
        fieldType: 'address',
        required: false,
        encrypted: false,
      },
      {
        label: 'Preferred Contact Method',
        extractionPath: 'contactInfo.preferredContactMethod',
        dbColumn: 'preferred_contact_method',
        fieldType: 'select',
        required: false,
        encrypted: false,
        options: [
          { value: 'email', label: 'Email' },
          { value: 'phone', label: 'Phone' },
          { value: 'mail', label: 'Mail' },
        ],
      },
    ],
  },

  // ========================================
  // SECTION 3: Employment Information
  // ========================================
  {
    id: 'employmentInfo',
    title: 'Employment Information',
    icon: 'Briefcase',
    dbTable: 'kyc_profiles',
    isArray: false,
    order: 3,
    fields: [
      {
        label: 'Employer Name',
        extractionPath: 'employmentInfo.employerName',
        dbColumn: 'employer_name',
        fieldType: 'text',
        required: false,
        encrypted: false,
      },
      {
        label: 'Occupation',
        extractionPath: 'employmentInfo.occupation',
        dbColumn: 'occupation',
        fieldType: 'text',
        required: false,
        encrypted: false,
      },
      {
        label: 'Employment Status',
        extractionPath: 'employmentInfo.employmentStatus',
        dbColumn: 'employment_status',
        fieldType: 'select',
        required: false,
        encrypted: false,
        options: [
          { value: 'employed', label: 'Employed' },
          { value: 'self-employed', label: 'Self-Employed' },
          { value: 'retired', label: 'Retired' },
          { value: 'unemployed', label: 'Unemployed' },
        ],
      },
      {
        label: 'Industry',
        extractionPath: 'employmentInfo.employmentIndustry',
        dbColumn: 'employment_industry',
        fieldType: 'text',
        required: false,
        encrypted: false,
      },
      {
        label: 'Years Employed',
        extractionPath: 'employmentInfo.yearsEmployed',
        dbColumn: 'years_employed',
        fieldType: 'number',
        required: false,
        encrypted: false,
        min: 0,
        max: 100,
      },
      {
        label: 'Annual Income',
        extractionPath: 'employmentInfo.annualIncome',
        dbColumn: 'annual_income',
        fieldType: 'currency',
        required: false,
        encrypted: false,
      },
      {
        label: 'Income Currency',
        extractionPath: 'employmentInfo.incomeCurrency',
        dbColumn: 'income_currency',
        fieldType: 'select',
        required: false,
        encrypted: false,
        options: [
          { value: 'CHF', label: 'CHF' },
          { value: 'EUR', label: 'EUR' },
          { value: 'USD', label: 'USD' },
        ],
      },
    ],
  },

  // ========================================
  // SECTION 4: Tax Information
  // ========================================
  {
    id: 'taxInfo',
    title: 'Tax Information',
    icon: 'FileText',
    dbTable: 'kyc_profiles',
    isArray: false,
    order: 4,
    fields: [
      {
        label: 'Tax Residence',
        extractionPath: 'taxInfo.taxResidence',
        dbColumn: 'tax_residence',
        fieldType: 'text',
        required: false,
        encrypted: false,
        helpText: 'ISO 2-letter country code',
      },
      {
        label: 'US Person',
        extractionPath: 'taxInfo.usPerson',
        dbColumn: 'us_person',
        fieldType: 'boolean',
        required: false,
        encrypted: false,
      },
      {
        label: 'Tax ID',
        extractionPath: 'taxInfo.taxId',
        dbColumn: 'tax_id',
        fieldType: 'text',
        required: false,
        encrypted: true,
      },
      {
        label: 'FATCA Status',
        extractionPath: 'taxInfo.fatcaStatus',
        dbColumn: 'fatca_status',
        fieldType: 'select',
        required: false,
        encrypted: false,
        options: [
          { value: 'exempt', label: 'Exempt' },
          { value: 'active', label: 'Active NFFE' },
          { value: 'passive', label: 'Passive NFFE' },
        ],
      },
    ],
  },

  // ========================================
  // SECTION 5: Wealth Sources (One-to-Many)
  // ========================================
  {
    id: 'wealthSources',
    title: 'Wealth Sources',
    icon: 'TrendingUp',
    dbTable: 'kyc_wealth_sources',
    isArray: true,
    order: 5,
    fields: [
      {
        label: 'Source Type',
        extractionPath: 'sourceType',
        dbColumn: 'source_type',
        fieldType: 'select',
        required: true,
        encrypted: false,
        options: [
          { value: 'employment', label: 'Employment' },
          { value: 'business', label: 'Business' },
          { value: 'inheritance', label: 'Inheritance' },
          { value: 'investment', label: 'Investment' },
          { value: 'real_estate', label: 'Real Estate' },
          { value: 'gift', label: 'Gift' },
          { value: 'other', label: 'Other' },
        ],
      },
      {
        label: 'Description',
        extractionPath: 'sourceDescription',
        dbColumn: 'source_description',
        fieldType: 'textarea',
        required: false,
        encrypted: false,
      },
      {
        label: 'Asset Value',
        extractionPath: 'assetValue',
        dbColumn: 'asset_value',
        fieldType: 'currency',
        required: false,
        encrypted: false,
      },
      {
        label: 'Asset Currency',
        extractionPath: 'assetCurrency',
        dbColumn: 'asset_currency',
        fieldType: 'select',
        required: false,
        encrypted: false,
        options: [
          { value: 'CHF', label: 'CHF' },
          { value: 'EUR', label: 'EUR' },
          { value: 'USD', label: 'USD' },
        ],
      },
      {
        label: 'Percentage of Total Wealth',
        extractionPath: 'percentage',
        dbColumn: 'percentage',
        fieldType: 'percentage',
        required: false,
        encrypted: false,
        min: 0,
        max: 100,
      },
    ],
  },

  // ========================================
  // SECTION 6: Income Sources (One-to-Many)
  // ========================================
  {
    id: 'incomeSources',
    title: 'Income Sources',
    icon: 'DollarSign',
    dbTable: 'kyc_income_sources',
    isArray: true,
    order: 6,
    fields: [
      {
        label: 'Income Type',
        extractionPath: 'incomeType',
        dbColumn: 'income_type',
        fieldType: 'select',
        required: true,
        encrypted: false,
        options: [
          { value: 'salary', label: 'Salary' },
          { value: 'business', label: 'Business Income' },
          { value: 'investment', label: 'Investment Income' },
          { value: 'rental', label: 'Rental Income' },
          { value: 'pension', label: 'Pension' },
          { value: 'other', label: 'Other' },
        ],
      },
      {
        label: 'Description',
        extractionPath: 'incomeDescription',
        dbColumn: 'income_description',
        fieldType: 'textarea',
        required: false,
        encrypted: false,
      },
      {
        label: 'Annual Amount',
        extractionPath: 'annualAmount',
        dbColumn: 'annual_amount',
        fieldType: 'currency',
        required: false,
        encrypted: false,
      },
      {
        label: 'Currency',
        extractionPath: 'currency',
        dbColumn: 'currency',
        fieldType: 'select',
        required: false,
        encrypted: false,
        options: [
          { value: 'CHF', label: 'CHF' },
          { value: 'EUR', label: 'EUR' },
          { value: 'USD', label: 'USD' },
        ],
      },
    ],
  },

  // ========================================
  // SECTION 7: Business Activities (One-to-Many)
  // ========================================
  {
    id: 'businessActivities',
    title: 'Business Activities',
    icon: 'Building',
    dbTable: 'kyc_business_activities',
    isArray: true,
    order: 7,
    fields: [
      {
        label: 'Business Name',
        extractionPath: 'businessName',
        dbColumn: 'business_name',
        fieldType: 'text',
        required: true,
        encrypted: false,
      },
      {
        label: 'Business Type',
        extractionPath: 'businessType',
        dbColumn: 'business_type',
        fieldType: 'text',
        required: false,
        encrypted: false,
      },
      {
        label: 'Industry',
        extractionPath: 'primaryIndustry',
        dbColumn: 'primary_industry',
        fieldType: 'text',
        required: false,
        encrypted: false,
      },
      {
        label: 'Ownership Percentage',
        extractionPath: 'ownershipPercentage',
        dbColumn: 'ownership_percentage',
        fieldType: 'percentage',
        required: false,
        encrypted: false,
        min: 0,
        max: 100,
      },
    ],
  },

  // ========================================
  // SECTION 8: PEP Status (One-to-One)
  // ========================================
  {
    id: 'pepStatus',
    title: 'PEP & Adverse Media',
    icon: 'Flag',
    dbTable: 'kyc_pep_status',
    isArray: false,
    order: 8,
    fields: [
      {
        label: 'Is PEP?',
        extractionPath: 'pepStatus.isPEP',
        dbColumn: 'is_pep',
        fieldType: 'boolean',
        required: false,
        encrypted: false,
      },
      {
        label: 'PEP Type',
        extractionPath: 'pepStatus.pepType',
        dbColumn: 'pep_type',
        fieldType: 'select',
        required: false,
        encrypted: false,
        options: [
          { value: 'domestic', label: 'Domestic PEP' },
          { value: 'foreign', label: 'Foreign PEP' },
          { value: 'international', label: 'International Organization PEP' },
        ],
      },
      {
        label: 'Position',
        extractionPath: 'pepStatus.position',
        dbColumn: 'position',
        fieldType: 'text',
        required: false,
        encrypted: false,
      },
    ],
  },

  // ========================================
  // SECTION 9: Adverse Media (One-to-Many)
  // ========================================
  {
    id: 'adverseMedia',
    title: 'Adverse Media Screening',
    icon: 'Flag',
    dbTable: 'kyc_adverse_media',
    isArray: true,
    order: 9,
    fields: [
      {
        label: 'Has Adverse Media',
        extractionPath: 'hasAdverseMedia',
        dbColumn: 'has_adverse_media',
        fieldType: 'boolean',
        required: true,
        encrypted: false,
      },
      {
        label: 'Severity Level',
        extractionPath: 'severityLevel',
        dbColumn: 'severity_level',
        fieldType: 'select',
        required: false,
        encrypted: false,
        options: [
          { value: 'low', label: 'Low' },
          { value: 'medium', label: 'Medium' },
          { value: 'high', label: 'High' },
          { value: 'critical', label: 'Critical' },
        ],
      },
      {
        label: 'Match Score',
        extractionPath: 'matchScore',
        dbColumn: 'match_score',
        fieldType: 'number',
        required: false,
        encrypted: false,
        min: 0,
        max: 1,
      },
      {
        label: 'Risk Impact',
        extractionPath: 'riskImpact',
        dbColumn: 'risk_impact',
        fieldType: 'select',
        required: false,
        encrypted: false,
        options: [
          { value: 'low', label: 'Low' },
          { value: 'medium', label: 'Medium' },
          { value: 'high', label: 'High' },
        ],
      },
      {
        label: 'Reviewed',
        extractionPath: 'reviewed',
        dbColumn: 'reviewed',
        fieldType: 'boolean',
        required: false,
        encrypted: false,
      },
    ],
  },

  // ========================================
  // SECTION 10: Personal Background (One-to-One)
  // ========================================
  {
    id: 'personalBackground',
    title: 'Personal Background',
    icon: 'GraduationCap',
    dbTable: 'kyc_personal_background',
    isArray: false,
    order: 10,
    fields: [
      {
        label: 'Education Level',
        extractionPath: 'personalBackground.educationLevel',
        dbColumn: 'education_level',
        fieldType: 'select',
        required: false,
        encrypted: false,
        options: [
          { value: 'high_school', label: 'High School' },
          { value: 'bachelor', label: "Bachelor's Degree" },
          { value: 'master', label: "Master's Degree" },
          { value: 'phd', label: 'Doctorate' },
        ],
      },
      {
        label: 'Family Composition',
        extractionPath: 'personalBackground.familyComposition',
        dbColumn: 'family_composition',
        fieldType: 'textarea',
        required: false,
        encrypted: true,
      },
      {
        label: 'Dependents Count',
        extractionPath: 'personalBackground.dependentsCount',
        dbColumn: 'dependents_count',
        fieldType: 'number',
        required: false,
        encrypted: false,
        min: 0,
      },
    ],
  },
];

/**
 * Get section definition by ID
 */
export function getSectionById(sectionId: string): SectionDefinition | undefined {
  return KYC_FIELD_MAPPING.find(section => section.id === sectionId);
}

/**
 * Get all sections sorted by order
 */
export function getAllSections(): SectionDefinition[] {
  return [...KYC_FIELD_MAPPING].sort((a, b) => a.order - b.order);
}

/**
 * Get field definition by extraction path
 */
export function getFieldByPath(extractionPath: string): FieldDefinition | undefined {
  for (const section of KYC_FIELD_MAPPING) {
    const field = section.fields.find(f => f.extractionPath === extractionPath);
    if (field) return field;
  }
  return undefined;
}
